// ---------------------------------------------------------
// Datei: CaptureModal.tsx
// Zweck: Modal für Dokumenten-Upload mit Kamera, Galerie oder Dateisystem
// Besonderheiten:
// - Auswahlmenü vor Kamera-Aktivierung (UX-Verbesserung)
// - Getrennte Optionen für Galerie (Bilder) und Dateisystem (alle Formate)
// - Queue-Integration für asynchrone Uploads
// - Optimistic Updates über Store
// - Stand 11.12.2025
// ---------------------------------------------------------

import React, { useState, useRef, useCallback, useMemo } from 'react';

interface CaptureModalProps {
  onClose: () => void;
  onCapture: (imageData: string) => void;
  title?: string;
  allowedTypes?: string[];
  isOpen?: boolean;
  checklistItemId?: string;
  phaseId?: string;
  projectId?: string;
  dealName?: string;
}

const CaptureModal: React.FC<CaptureModalProps> = ({
  onClose,
  onCapture,
  title = 'Dokument hinzufügen',
  allowedTypes = ['image/jpeg', 'image/png'],
  isOpen = true,
}) => {
  const debugLog = (...args: any[]) => {
    console.log('[debug-upload][CaptureModal]', ...args);
  };
  // State-Definitionen
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  
  // Modi: "selection" = Auswahlmenü, "camera" = Kamera aktiv, "files" = Dateisystem
  const [mode, setMode] = useState<'selection' | 'camera' | 'files'>('selection');

  // Refs für Video, Canvas und File-Input
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);
  

  // Accept-String für Dateisystem (alle erlaubten Typen)
  const filesAccept = useMemo(() => {
    if (allowedTypes.length === 0) {
      return 'image/*,application/pdf';
    }
    return allowedTypes.join(',');
  }, [allowedTypes]);

  // Kamera aktivieren
  const startCamera = useCallback(async () => {
    try {
      debugLog('startCamera: requesting media');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraActive(true);
        debugLog('startCamera: active');
      }
    } catch (error) {
      console.error('Fehler beim Zugriff auf die Kamera:', error);
      debugLog('startCamera: failed', error);
      alert('Kamerazugriff nicht möglich. Bitte erteilen Sie die Berechtigung.');
      // Bei Fehler zurück zur Auswahl
      setMode('selection');
    }
  }, []);

  // Kamera deaktivieren
  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
    }
  }, []);

  // Bild aufnehmen
  const takePicture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    // Canvas-Größe an Video anpassen
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Bild auf Canvas zeichnen
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Bild als Base64 speichern
    const imageData = canvas.toDataURL('image/png'); // Verlustfrei
    setImagePreview(imageData);
    stopCamera();
  }, [stopCamera]);

  // Aufnahme speichern
  const saveCapture = useCallback(() => {
    if (!imagePreview) return;
    
    // Bild an Parent-Komponente übergeben (Parent ist verantwortlich für Queue)
    onCapture(imagePreview);

    // Modal schließen und Zustand zurücksetzen
    onClose();
    setImagePreview(null);
    setMode('selection');
  }, [imagePreview, onCapture, onClose]);

  // Aufnahme verwerfen
  const discardCapture = useCallback(() => {
    setImagePreview(null);
    setMode('selection');
    stopCamera();
  }, [stopCamera]);

  // Datei aus Galerie/Dateisystem auswählen
  // In CaptureModal.tsx - ersetze die handleFileSelect Funktion: 

// Datei aus Galerie/Dateisystem auswählen
const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target. files?.[0];
  
  // Bei Abbruch zurück zur Selection
  if (!file) {
    debugLog('handleFileSelect: cancel/no file');
    setMode('selection');
    if (filesInputRef.current) filesInputRef.current.value = '';
    return;
  }

  // Kamera stoppen falls aktiv
  if (isCameraActive) {
    stopCamera();
  }

  try {
    debugLog('handleFileSelect: file', { name: file.name, type: file.type, size: file.size });
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?. result as string;
      if (result) {
        setImagePreview(result);
      }
    };
    reader.onerror = () => {
      console.error('Fehler beim Laden der Datei');
      alert('Datei konnte nicht geladen werden.');
      setMode('selection');
    };
    reader. readAsDataURL(file);
  } catch (error) {
    console.error('Fehler beim Laden der Datei:', error);
    alert('Datei konnte nicht geladen werden.');
    setMode('selection');
  }
  
  // Input zurücksetzen
  if (event.target) {
    event. target.value = '';
  }
}, [isCameraActive, stopCamera]);

  // Galerie öffnen (nur Bilder)
  // Dateisystem öffnen (alle Formate)
  const openFiles = useCallback(() => {
    setMode('files');
    debugLog('openFiles', { accept: filesAccept });
    filesInputRef.current?.click();
  }, []);

  // Kamera-Modus aktivieren
  const activateCamera = useCallback(() => {
    debugLog('activateCamera');
    setMode('camera');
  }, []);

  // Modal schließen und Ressourcen freigeben
  const handleClose = useCallback(() => {
    stopCamera();
    setImagePreview(null);
    setMode('selection');
    onClose();
  }, [stopCamera, onClose]);

  // Kamera nur starten, wenn mode auf "camera" wechselt
  React.useEffect(() => {
    if (!isOpen) return;

    if (mode === 'camera' && !isCameraActive && !imagePreview) {
      startCamera();
    }
    
    // Cleanup beim Unmount oder Mode-Wechsel
    return () => {
      if (isCameraActive && mode !== 'camera') {
        stopCamera();
      }
    };
  }, [isOpen, mode, isCameraActive, imagePreview, startCamera, stopCamera]);

  // Cleanup beim Schließen des Modals
  React.useEffect(() => {
    if (!isOpen && isCameraActive) {
      stopCamera();
    }
  }, [isOpen, isCameraActive, stopCamera]);
  React.useEffect(() => {
    if (isOpen) {
    debugLog('open', {
      mode,
      isCameraActive,
      allowedTypes,
      userAgent: navigator.userAgent
    });
    }
  }, [isOpen, mode, isCameraActive, allowedTypes]);

  // Nach den anderen useEffects in CaptureModal.tsx hinzufügen:

// Handler für File-Input Cancel Event (wenn User Dialog schließt ohne Auswahl)
React.useEffect(() => {
  const handleCancel = () => {
    // Bei Abbruch der Dateiauswahl:  zurück zur Selection
    if (mode === 'files') {
      setMode('selection');
    }
  };

  const filesInput = filesInputRef.current;

  // 'cancel' Event (moderner Browsers) oder 'click' für Fallback
  filesInput?.addEventListener('cancel', handleCancel);

  return () => {
    filesInput?.removeEventListener('cancel', handleCancel);
  };
  }, [mode]);

  if (!isOpen) return null;

  return (
    // pb-20: Platz für BottomNavigation
    <div data-testid="capture-modal" className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[calc(90vh-60px)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {imagePreview ? 'Vorschau' : title}
          </h3>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
            aria-label="Schließen"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="p-4 flex-1 overflow-auto">

          {/* Auswahlmenü */}
          {mode === 'selection' && !imagePreview && (
            <div className="flex flex-col items-stretch justify-center py-8 space-y-4">
              <button
                onClick={activateCamera}
                data-testid="camera-button"
                className="btn btn-primary flex items-center justify-center py-4"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Kamera öffnen
              </button>

              <button
                onClick={openFiles}
                data-testid="choose-file-button"
                className="btn btn-secondary flex items-center justify-center py-4"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Datei auswählen (PDF, Bilder, andere Formate)
              </button>

            </div>
          )}

          {/* Kamera aktiv */}
          {mode === 'camera' && !imagePreview && (
            <div className="relative bg-black rounded overflow-hidden aspect-video">
              <video 
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              ></video>
            </div>
          )}

          {/* Bild Vorschau */}
          {imagePreview && (
            <div className="relative bg-black rounded overflow-hidden">
              <img 
                src={imagePreview} 
                alt="Vorschau" 
                className="w-full object-contain max-h-[60vh]"
              />
            </div>
          )}

          {/* Hidden File Input – MUSS außerhalb des mode==='selection' Blocks stehen,
              sonst wird es beim Mode-Wechsel unmounted und onChange feuert nicht */}
          <input
            ref={filesInputRef}
            type="file"
            accept={filesAccept}
            onChange={handleFileSelect}
            className="hidden"
          />
          {/* Hidden Canvas für Foto-Aufnahme */}
          <canvas ref={canvasRef} className="hidden"></canvas>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t dark:border-gray-700 flex justify-center gap-4">
          {/* Kamera-Modus: Aufnahme-Button */}
          {mode === 'camera' && !imagePreview && (
            <>
              <button
                onClick={() => {
                  stopCamera();
                  setMode('selection');
                }}
                className="btn btn-secondary flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Zurück
              </button>
              <button
                onClick={takePicture}
                data-testid="capture-button"
                className="btn btn-primary flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Aufnehmen
              </button>
            </>
          )}

          {/* Vorschau-Modus: Speichern/Verwerfen */}
          {imagePreview && (
            <>
              <button
                onClick={discardCapture}
                className="btn btn-secondary flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Verwerfen
              </button>
              <button
                onClick={saveCapture}
                data-testid="upload-button"
                className="btn btn-primary flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Speichern
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaptureModal;
